import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import classnames from 'classnames';
import {Anchor, Card, Heading, Text, ButtonGroup, Button, Icon} from '@albert-io/atomic';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {getModelForResourceType} from 'resources/modelRegistry';
import {pluralize} from 'lib/stringUtils';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import InviteDropdownBtn from 'client/Classrooms/Classroom/Teacher/ManageRoster/InviteDropdownBtn/InviteDropdownBtn.react';
import {createManageRosterContextState} from 'client/Classrooms/Classroom/Teacher/ManageRoster/helpers';
import {getClassroomsForTeacherClassroomsDashboardQuery} from 'client/Classrooms/Teacher/ClassroomsList/helpers';
import {LICENSE_SEAT_WARNING_THRESHOLD} from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';
import './teacher-classes-card.scss';

export default class TeacherClassesCard extends React.Component {
  static propTypes = {
    classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1')),
    countOfPendingInvitations: PropTypes.number,
    countOfStudents: PropTypes.number,
    isArchived: PropTypes.bool
  };

  constructor(props) {
    super(props);

    const {classroom, countOfPendingInvitations, countOfStudents} = props;
    const classId = classroom.getId();
    const stateObject = createManageRosterContextState(classId, this);

    /**
     * Student and invitation totals are available on the context object as well,
     * but to make managing state easier, we want to treat the teacher classrooms
     * dashboard query as the source of truth. Therefore we pull these values from
     * the classroom available in our props. Any changes are managed by the `key`
     * in such a way as to emulate getDerivedStateFromProps.
     *
     * With these numbers in hand, we can also save ourselves an additional set of API
     * requests by manually setting the isReady flag.
     *
     * @see TeacherClasses.react.js
     */
    this.state = {
      ...stateObject,
      isReady: true,
      /* eslint-disable-next-line react/no-unused-state */
      classroom,
      countOfPendingInvitations,
      countOfStudents
    };
  }

  componentDidUpdate(_, prevState) {
    // Our context consumers alert the provider to changes via the isReady flag.
    // When this flag has been toggled, it's a signal we need to re-fetch the classroom list.
    if (prevState.isReady && !this.state.isReady) {
      const {resourcePath} = getClassroomsForTeacherClassroomsDashboardQuery().done();
      invalidatePartialInterest({resourcePath});
    }
  }

  render() {
    const {classroom} = this.props;
    const seatsRemaining = classroom.getMeta().getCountOfSchoolLicenseSeatsRemaining();
    const {countOfPendingInvitations, countOfStudents, setModalToShow, modalToShow} = this.state;
    const ModalRenderer = manageRosterModalCollection[this.state.modalToShow];

    return (
      <ManageRosterContext.Provider value={this.state}>
        <Card
          className={classnames('teacher-classes-card u-pad_4', {
            'teacher-classes-card--archived': this.props.isArchived
          })}
        >
          <div className='teacher-classes-card__text u-mar-b_6'>
            <Heading
              as='h1'
              className='teacher-classes-card__heading u-mar-b_1'
              size='m'
              emphasis='high'
            >
              {classroom.getName()}
            </Heading>
            <Text className='u-mar-b_1' emphasis='medium'>
              {`${countOfStudents} ${pluralize('enrolled student', countOfStudents)}`}
            </Text>
            {countOfPendingInvitations !== 0 && !this.props.isArchived && (
              <div className='teacher-classes-card__pending-invitations'>
                <Text italic>
                  {`${countOfPendingInvitations} ${pluralize(
                    'pending invitation',
                    countOfPendingInvitations
                  )}`}
                </Text>
                &nbsp;
                <Anchor
                  onClick={() => setModalToShow('bulkResend')}
                  underlined
                  className='teacher-classes-card__resend-link'
                  tabIndex='0'
                  aria-label='Resend all pending invitations'
                >
                  (Resend all)
                </Anchor>
              </div>
            )}
          </div>
          <ButtonGroup positionAtBottom direction='column' fillColumn>
            {this.props.isArchived ? null : (
              <InviteDropdownBtn
                shouldDisplayHardCapMessage={
                  classroom.getSchool().isHardCap() &&
                  seatsRemaining <= LICENSE_SEAT_WARNING_THRESHOLD
                }
                hardCapSeatsRemaining={seatsRemaining}
                triggerBtn={
                  <Button
                    aria-label={`Invite students to this class, ${this.props.classroom.getName()}`}
                    variant='text'
                    className='teacher-classes-card__invite-students-button'
                  >
                    Invite students
                    <Icon icon='caret-down' className='u-mar-l_1' />
                  </Button>
                }
              />
            )}
            <Button
              tabIndex='0'
              aria-label={`View this class, ${classroom.getName()}`}
              as={Link}
              to={`/classes/${classroom.getId()}/assignments`}
            >
              {this.props.isArchived ? 'View archived class' : 'View class'}
            </Button>
          </ButtonGroup>
        </Card>
        {modalToShow && <ModalRenderer handleClose={() => this.setState({modalToShow: ''})} />}
      </ManageRosterContext.Provider>
    );
  }
}
